import { FormField } from '@batiflow/ui';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Switch,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DomainList } from '@/features/admin/policies/domain-list';
import {
  DlpLockedNotice,
  DlpUpgradePrompt,
  ShareLinksSwitch,
  tighteningLocked,
  useDlpEntitled,
} from '@/features/share-links';
import { SettingsSection } from '@/features/admin/components/settings-section';
import { useNotify } from '@/shared/hooks/use-notify';
import { useUpdateSpace } from './api';
import { CloseSpaceSection } from './sharing/close-space-section';
import {
  buildSettingsPatch,
  isClosed,
  isPlanLimit,
  isStepUpRequired,
  spaceErrorMessage,
  valuesOf,
  type SettingsValues,
  type Space,
} from './model';

const useStyles = __css({"page":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f13377fj","D305fb":"f1bjjmyr"},"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f13377fj","D305fb":"f1bjjmyr"},"fields":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"name":{"D305fb":"f1f0uekf"},"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l"}});

export interface SpaceSettingsTabProps {
  space: Space;
  /** The reader may close the space: owner or administrator of the host (`org.policy.update`). */
  canClose?: boolean;
}

/**
 * The Settings tab (host managers only: the server lists it in `tabs` for `can_manage`): the
 * name and the rules of the space. Saving sends what changed with PATCH; the API asks for a
 * recent identity check (step-up), which the app's step-up dialog handles before replaying it.
 */
export function SpaceSettingsTab({ space, canClose = false }: SpaceSettingsTabProps) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const notify = useNotify();
  const update = useUpdateSpace(space.id);
  const closed = isClosed(space);
  // Tightening a rule needs feature.dlp (Business and Enterprise); loosening never does.
  const dlp = useDlpEntitled();
  const { policy } = space;

  const [values, setValues] = useState<SettingsValues>(() => valuesOf(space));
  const [nameError, setNameError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [planLimit, setPlanLimit] = useState(false);

  const change = (patch: Partial<SettingsValues>) => {
    setValues((current) => ({ ...current, ...patch }));
  };

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (update.isPending || closed) return;
    setFailure(null);
    setPlanLimit(false);
    setNameError(null);
    const patch = buildSettingsPatch(space, values);
    if (!patch.ok) {
      if (patch.reason === 'invalid') setFailure(t('settings.errors.validation'));
      else setNameError(t(`settings.errors.${patch.reason}`));
      return;
    }
    if (patch.data === null) {
      notify({ intent: 'info', message: t('settings.noChange') });
      return;
    }
    try {
      const saved = await update.mutateAsync({ spaceId: space.id, data: patch.data });
      setValues(valuesOf(saved));
      notify({ intent: 'success', message: t('settings.saved') });
    } catch (error) {
      // STEP_UP_REQUIRED: the app's step-up dialog is open now and replays this save afterwards.
      if (isStepUpRequired(error)) return;
      if (isPlanLimit(error)) {
        setPlanLimit(true);
        return;
      }
      setFailure(spaceErrorMessage(t, tCommon, error, 'settings'));
    }
  };

  return (
    <div className={styles.page}>
      <form
        noValidate
        className={styles.form}
        aria-label={t('settings.region')}
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        {closed ? (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>{t('settings.closedNotice')}</MessageBarBody>
          </MessageBar>
        ) : null}

        <SettingsSection
          title={t('settings.general.title')}
          description={t('settings.general.description')}
        >
          <FormField
            required
            className={styles.name}
            label={t('settings.name.label')}
            error={nameError}
          >
            <Input
              value={values.name}
              disabled={closed}
              maxLength={400}
              onChange={(_event, data) => {
                change({ name: data.value });
                setNameError(null);
              }}
            />
          </FormField>
        </SettingsSection>

        <SettingsSection
          title={t('settings.policy.title')}
          description={t('settings.policy.description')}
        >
          <div className={styles.fields}>
            {dlp || closed ? null : <DlpLockedNotice />}
            <Switch
              label={t('settings.download.label')}
              checked={values.downloadAllowed}
              disabled={closed || tighteningLocked(dlp, policy.downloadAllowed)}
              onChange={(_event, data) => {
                change({ downloadAllowed: data.checked });
              }}
            />
            <p className={styles.note}>{t('settings.download.hint')}</p>
            <Switch
              label={t('settings.watermark.label')}
              checked={values.watermark}
              disabled={closed || tighteningLocked(dlp, !policy.watermark)}
              onChange={(_event, data) => {
                change({ watermark: data.checked });
              }}
            />
            <p className={styles.note}>{t('settings.watermark.hint')}</p>
            <Switch
              label={t('settings.print.label')}
              checked={values.printAllowed}
              disabled={closed || tighteningLocked(dlp, policy.printAllowed)}
              onChange={(_event, data) => {
                change({ printAllowed: data.checked });
              }}
            />
            <p className={styles.note}>{t('settings.print.hint')}</p>
            <ShareLinksSwitch
              checked={values.shareLinksAllowed}
              disabled={closed || tighteningLocked(dlp, policy.shareLinksAllowed)}
              onChange={(shareLinksAllowed) => {
                change({ shareLinksAllowed });
              }}
            />
            {closed ? null : (
              <DomainList
                domains={values.guestDomains}
                texts={{
                  label: t('settings.domains.label'),
                  hint: t('settings.domains.hint'),
                  empty: t('settings.domains.empty'),
                }}
                onChange={(guestDomains) => {
                  change({ guestDomains });
                }}
              />
            )}
          </div>
        </SettingsSection>

        {planLimit ? <DlpUpgradePrompt orgId={space.host.organizationId} /> : null}
        {failure ? (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>{t('settings.failedTitle')}</MessageBarTitle>
              {failure}
            </MessageBarBody>
          </MessageBar>
        ) : null}
        {closed ? null : (
          <div className={styles.actions}>
            <Button type="submit" appearance="primary" disabledFocusable={update.isPending}>
              {update.isPending ? t('settings.saving') : t('settings.save')}
            </Button>
            <p className={styles.note}>{t('settings.stepUpNote')}</p>
          </div>
        )}
      </form>
      {canClose && !closed ? <CloseSpaceSection space={space} /> : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/space-settings-tab.griffel.css";